'use client';

import { Suspense } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HeroMark } from '@/components/layout/HeroMark';
import { Scale, Loader2 } from 'lucide-react';

function SignInContent() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const callbackUrl = searchParams.get('callbackUrl') || '/chat';
  const error = searchParams.get('error');

  // Redirect if already signed in
  useEffect(() => {
    if (status === 'authenticated') {
      router.push(callbackUrl);
    }
  }, [status, router, callbackUrl]);

  const handleSignIn = async () => {
    setIsLoading(true);
    await signIn('google', { callbackUrl });
  };

  if (status === 'loading') {
    return (
      <Card className="w-full max-w-md p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-4">Preverjam prijavo...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md p-8">
      <div className="text-center mb-8">
        <HeroMark className="w-16 h-16 mb-5">
          <Scale className="h-8 w-8" />
        </HeroMark>
        <h1 className="text-4xl font-semibold tracking-[0.1em]" style={{ fontFamily: 'var(--font-cormorant), serif' }}>TRIBONIANUS</h1>
        <p className="text-[11px] uppercase tracking-[0.28em] text-brand mt-2" style={{ fontFamily: 'var(--font-geist-mono), monospace' }}>
          Ius ex machina
        </p>
        <p className="text-muted-foreground mt-1 text-sm">
          AI pravni pomočnik
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive text-center">
            {error === 'AccessDenied'
              ? 'Dostop zavrnjen. Vaš e-poštni naslov ni na seznamu dovoljenih uporabnikov.'
              : 'Prišlo je do napake pri prijavi. Poskusite znova.'}
          </p>
        </div>
      )}

      <Button
        onClick={handleSignIn}
        disabled={isLoading}
        className="w-full h-12 text-base"
        variant="outline"
      >
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
        ) : (
          <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        )}
        Prijava z Google računom
      </Button>

      <p className="text-xs text-muted-foreground text-center mt-6">
        Za dostop potrebujete odobren e-poštni naslov.
        <br />
        Kontaktirajte administratorja za dostop.
      </p>
    </Card>
  );
}

function SignInFallback() {
  return (
    <Card className="w-full max-w-md p-8">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground mt-4">Nalaganje...</p>
      </div>
    </Card>
  );
}

export default function SignInPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Suspense fallback={<SignInFallback />}>
        <SignInContent />
      </Suspense>
    </div>
  );
}
